<template>
  <v-dialog v-model="showAssignPlaylist" persistent max-width="600px">
    <AddEditGroupsDialog
      ref="addEditGroupInDialog"
      @changeEvent="doneNewGroup"
    />
    <v-card>
      <v-card-title class="headline grey lighten-3">
        <span class="headline">
          <v-icon class="mr-2">fal fa-users-class</v-icon>Assign Playlist to
          User(s) or Group(s)
        </span>
        <v-spacer></v-spacer>
        <button
          type="button"
          class="btn btn-light btn-elevate-hover btn-circle btn-icon btn-sm"
          :disabled="isLoading"
          @click="showAssignPlaylist = false"
        >
          <i class="fal fa-times"></i>
        </button>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12" sm="12" md="12">
              <multiselect
                v-model="playlistSelect"
                :options="playlistItems"
                label="name"
                placeholder="Select Playlist"
                v-validate="'required'"
                name="playlist"
              ></multiselect>
              <span
                class="kt-font-danger validate-error"
                v-show="errors.has('playlist')"
              >
                {{ errors.first('playlist') }}
              </span>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-checkbox
                v-model="assignToAll"
                label="Assign to All"
              ></v-checkbox>
            </v-col>
            <v-col cols="12" sm="6" md="8">
              <assign-dropdown
                :value="selectedUsers"
                :set-drop-down-value="setDropdownValue"
              ></assign-dropdown>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="12" md="12">
              <v-autocomplete
                v-model="selectedGroups"
                :items="groups"
                item-text="name"
                item-value="id"
                :menu-props="{ closeOnClick: true }"
                label="Assign to Groups"
                multiple
                append-icon=""
                prepend-icon="fal fa-user-friends"
              >
                <template v-slot:selection="{ item }">
                  <v-chip :small="selectedGroups.length >= 4">
                    <span>{{ item.name }}</span>
                  </v-chip>
                </template>

                <template v-if="canUpdateCreateGroup" v-slot:prepend-item>
                  <v-list-item ripple @click="createNewGroup()">
                    <v-list-item-content>
                      <v-list-item-title
                        style="
                          font-size: 17px;
                          display: flex;
                          justify-content: center;
                          gap: 7px;
                          align-items: center;
                        "
                      >
                        <i class="fas fa-plus"></i>
                        <span>Create New Group</span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-2"></v-divider>
                </template>

                <template v-slot:item="{ item, attrs, on }">
                  <v-list-item v-on="on" v-bind="attrs" #default="{ active }">
                    <v-list-item-action>
                      <v-checkbox :input-value="active"></v-checkbox>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-row no-gutters align="center">
                        <span>{{ item.name }}</span>
                        <v-spacer></v-spacer>
                        <v-btn
                          v-if="canUpdateCreateGroup"
                          icon
                          large
                          v-on:click.stop="editGroup(item)"
                        >
                          <v-icon medium>fal fa-edit</v-icon>
                        </v-btn>
                      </v-row>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" sm="12" md="12">
              <v-menu
                ref="dueDateMenu"
                v-model="dueDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="dueDate"
                    label="Due Date"
                    persistent-hint
                    prepend-icon="fal fa-calendar"
                    v-on="on"
                    readonly
                    v-bind="attrs"
                    @blur="temporaryDate = parseDate(dueDate)"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="temporaryDate"
                  no-title
                  @input="dueDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" :loading="isLoading" text @click="assign"
          >Save</v-btn
        >
        <v-btn color="blue-grey" text @click="showAssignPlaylist = false"
          >Close</v-btn
        >
      </v-card-actions>
    </v-card>

    <v-overlay :value="isLoading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-dialog>
</template>
<script>
import {
  union as lodashUnion,
  findIndex as _findIndex,
  each as _each,
  isEmpty as _isEmpty,
  debounce as _debounce,
  map as _map,
} from 'lodash-es';
import AssignDropdown from '../../client-lms/AssignDropdown.vue';
import AddEditGroupsDialog from '@/components/user-management/user/partial/AddNewGroupDialog.vue';
import { canUpdateCreateGroups } from './../../../helper/user/roleHelper';

export default {
  name: 'ToolbarAssignPlaylist',
  components: { AssignDropdown, AddEditGroupsDialog },
  props: {
    value: Boolean,
  },

  data() {
    return {
      date: '',
      selectedGroups: [],
      groups: [],
      selectedUsers: [],
      playlistSelect: '',
      assignToAll: false,
      dueDate: this.formatDate(new Date().toISOString().substr(0, 10)),
      dueDateMenu: false,
      usersSearchResult: [],
      searchUsers: null,
      temporaryDate: new Date().toISOString().substr(0, 10),
      isLoading: false,
      selectLoading: {
        users: true,
      },
      playlistItems: [],
    };
  },

  computed: {
    canUpdateCreateGroup() {
      return canUpdateCreateGroups(this.$auth.user().roles);
    },
    showAssignPlaylist: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    showSelectedUsers() {
      return this.selectedUsers.length > 0;
    },
    users() {
      return lodashUnion(this.selectedUsers, this.usersSearchResult);
    },
    userValidate() {
      return this.selectedGroups.length !== 0 || this.assignToAll
        ? ''
        : 'required';
    },
  },

  created() {
    console.log(this.value);
    this.getGroups();
    this.getFirstTenUsers();
    this.getPlaylists();
  },

  methods: {
    setDropdownValue(value) {
      this.selectedUsers = value;
    },
    getFirstTenUsers() {
      this.selectLoading.users = true;
      this.$http
        .get('get-random-users', {
          params: {
            users: 10,
          },
        })
        .then(({ data }) => {
          _each(data, (user) => {
            const index = _findIndex(this.usersSearchResult, { id: user.id });
            if (index === -1) {
              this.usersSearchResult.push(user);
            }
          });
          this.selectLoading.users = false;
        })
        .catch((error) => {
          this.selectLoading.users = false;
        });
    },
    getPlaylists() {
      this.$http.get('/playlist').then((response) => {
        this.playlistItems = response.data;
      });
    },
    getGroups() {
      this.$http
        .get('/groups')
        .then((response) => {
          _each(response.data, (group) => {
            this.groups.push(group);
          });
        })
        .catch((err) => {});
    },
    searchUser: _debounce(function (user) {
      this.fetchUsers(user);
    }, 500),
    fetchUsers(filter) {
      const params = {};
      if (filter) {
        params.filter = filter;
      }

      this.selectLoading.users = true;

      this.$http
        .get('/playlist/assign', { params })
        .then(({ data }) => {
          _each(data, (user) => {
            const index = _findIndex(this.usersSearchResult, { id: user.id });
            if (index === -1) {
              this.usersSearchResult.push(user);
            }
          });

          this.selectLoading.users = false;
        })
        .catch(() => {
          this.selectLoading.users = false;
        });
    },
    assign() {
      const selectedUsers = _map(this.selectedUsers, (user) => {
        return user.id;
      });
      const users = this.assignToAll ? 'all' : selectedUsers;
      this.$validator.validate('*').then((result) => {
        if (result) {
          this.isLoading = true;
          this.$http
            .post('/playlist/assign', {
              playlist_id: this.playlistSelect.id,
              users,
              due_date: this.dueDate,
              groups: this.selectedGroups,
            })
            .then(() => {
              this.showAssignPlaylist = false;
              this.$notify('success', 'Playlist successfully assigned!');
              // reset to default
              this.selectedUsers = [];
              this.selectedGroups = [];
              this.playlistSelect = '';
              this.assignToAll = false;
              this.dueDate = this.formatDate(
                new Date().toISOString().substr(0, 10)
              );
              this.value = false;
              this.isLoading = false;
            })
            .catch(() => {
              this.showAssignPlaylist = false;
              this.$notify('warning', 'Something went wrong!');
              this.value = false;
              this.isLoading = false;
            });
        }
      });
    },
    selectedUsersChanged() {
      this.searchUsers = '';
    },

    formatDate(date) {
      if (!date) return null;
      const [year, month, day] = date.split('-');
      return `${month}/${day}/${year}`;
    },

    parseDate(date) {
      if (!date) return null;
      const [month, day, year] = date.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    },
    editGroup(group) {
      this.hideMenuContentWhenOpeningNewDialog();
      this.$refs['addEditGroupInDialog'].toggleDialog('Edit Group', group);
    },
    hideMenuContentWhenOpeningNewDialog() {
      let el = document.getElementsByClassName('menuable__content__active');
      el[0].classList.add('display-none');
    },
    createNewGroup() {
      this.hideMenuContentWhenOpeningNewDialog();
      this.$refs['addEditGroupInDialog'].toggleDialog(
        'Create New Group',
        false
      );
    },
    doneNewGroup(item) {
      if (item.isUpdate) {
        let indexToModify = this.groups.findIndex(
          (group) => group.id === item.id
        );
        if (indexToModify > -1) {
          this.groups[indexToModify].name = item.name;
          this.groups[indexToModify].users = item.users;
        }

        if (!this.selectedGroups.includes(item.id)) {
          this.selectedGroups.push(item.id);
        }
      } else {
        if (item) this.groups.push(item);
        if (item.id) this.selectedGroups.push(item.id);
      }
    },
  },

  watch: {
    searchUsers(val) {
      if (val) {
        this.fetchUsers(val);
      }
    },

    temporaryDate(val) {
      this.dueDate = this.formatDate(this.temporaryDate);
    },
  },
};
</script>

<style scoped>
@media only screen and (max-width: 600px) {
  .headline {
    font-size: 10px !important;
  }
}
</style>
